"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBooleanLocalStorage = void 0;
exports.useLocalStorage = useLocalStorage;
var react_1 = require("react");
/**
 * We also need a custom event, as local storage changes are not dispatched within the same window/tab
 */
var LocalStorageChangedEvent = 'onLocalStorageChange';
function createLocalStorageChangedEvent(payload) {
    return new CustomEvent(LocalStorageChangedEvent, { detail: payload });
}
function isLocalStorageChangedEvent(event) {
    return (event instanceof CustomEvent &&
        typeof event.detail === 'object' &&
        typeof event.detail.key === 'string' &&
        (event.detail.value === undefined || typeof event.detail.value === 'string'));
}
/* In case local storage is not supported, we also have a in-memory storage option */
var InMemoryStorage = /** @class */ (function () {
    function InMemoryStorage() {
        this.storage = {};
    }
    InMemoryStorage.prototype.getItem = function (key) {
        var value = this.storage[key];
        return value === undefined ? null : value;
    };
    InMemoryStorage.prototype.setItem = function (key, value) {
        this.storage[key] = value;
    };
    InMemoryStorage.prototype.removeItem = function (key) {
        delete this.storage[key];
    };
    return InMemoryStorage;
}());
function supportsLocalStorage() {
    try {
        if (window === undefined ||
            window.localStorage === undefined ||
            typeof window.localStorage !== 'object' ||
            typeof window.localStorage.setItem !== 'function' ||
            typeof window.localStorage.removeItem !== 'function') {
            return false;
        }
    }
    catch (e) {
        return false;
    }
    try {
        var testKey = '__test_access_key__';
        window.localStorage.setItem(testKey, testKey);
        window.localStorage.removeItem(testKey);
        return true;
    }
    catch (e) {
        return false;
    }
}
var storage = supportsLocalStorage() ? window.localStorage : new InMemoryStorage();
var readLocalStorage = function (key, fallbackValue) {
    var value = storage.getItem(key);
    return value === null ? fallbackValue : value;
};
function useLocalStorage(key, fallbackValue, updateOnFallbackValueChange) {
    if (updateOnFallbackValueChange === void 0) { updateOnFallbackValueChange = false; }
    var _a = (0, react_1.useState)(readLocalStorage(key, fallbackValue)), item = _a[0], setItem = _a[1];
    var writeValueToStorage = (0, react_1.useCallback)(function (value) {
        if (value === undefined) {
            storage.removeItem(key);
        }
        else {
            storage.setItem(key, value);
        }
        setItem(value);
        window.dispatchEvent(createLocalStorageChangedEvent({ key: key, value: value }));
    }, [key]);
    (0, react_1.useEffect)(function () {
        if (updateOnFallbackValueChange) {
            setItem(readLocalStorage(key, fallbackValue));
        }
    }, [fallbackValue, updateOnFallbackValueChange]);
    (0, react_1.useEffect)(function () {
        function onStorageChanged(event) {
            if (event instanceof StorageEvent) {
                var eventKey = event.key;
                if (eventKey !== key) {
                    return;
                }
                return setItem(event.newValue !== null ? event.newValue : fallbackValue);
            }
            if (isLocalStorageChangedEvent(event)) {
                var _a = event.detail, eventKey = _a.key, value = _a.value;
                if (eventKey !== key) {
                    return;
                }
                return setItem(value);
            }
        }
        window.addEventListener('storage', onStorageChanged);
        window.addEventListener(LocalStorageChangedEvent, onStorageChanged);
        return function () {
            window.removeEventListener('storage', onStorageChanged);
            window.removeEventListener(LocalStorageChangedEvent, onStorageChanged);
        };
    }, [key]);
    return [item, writeValueToStorage];
}
var convertBooleanToString = function (value) { return (value ? 'true' : 'false'); };
/**
 * A specialised version of useLocalStorage for dealing with boolean values
 * As local storage can only contain string types, this wrapper handles the marshalling of the boolean
 *
 * @param key key of the local storage property to read/manipulate
 * @param initialValue initial value, in case the property is not present in local storage
 * @returns tuple of the value as well as a setter for changing it
 */
var useBooleanLocalStorage = function (key, initialValue) {
    if (initialValue === void 0) { initialValue = true; }
    var _a = useLocalStorage(key, convertBooleanToString(initialValue)), value = _a[0], setValue = _a[1];
    var stableSetValue = (0, react_1.useCallback)(function (value) {
        setValue(convertBooleanToString(value));
    }, [setValue]);
    return [value === 'true', stableSetValue];
};
exports.useBooleanLocalStorage = useBooleanLocalStorage;
//# sourceMappingURL=use_local_storage.js.map